import { useEffect } from "react";
import PembrokePinesHeroSection from "./components/HeroSection";
import PembrokePinesStickyNav from "./components/StickyNav";
import PembrokePinesProcessingTimeSection from "./components/ProcessingTimeSection";
import PembrokePinesServicesSection from "./components/ServicesSection";
import PembrokePinesLocalAreaSection from "./components/LocalAreaSection";
import PembrokePinesTravelTimingSection from "./components/TravelTimingSection";
import PembrokePinesDestinationsSection from "./components/DestinationsSection";
import PembrokePinesTestimonialsSection from "./components/TestimonialsSection";
import PembrokePinesAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import PembrokePinesComparisonSection from "./components/ComparisonSection";
import PembrokePinesMistakesSection from "./components/MistakesSection";
import PembrokePinesHowItWorksSection from "./components/HowItWorksSection";
import PembrokePinesFAQSection, { faqs } from "./components/FAQSection";
import PembrokePinesHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import PembrokePinesFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function PembrokePinesPage() {
  useEffect(() => {
    document.title = "Emergency Passport Pembroke Pines, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/pembroke-pines/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Pembroke Pines, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Broward County travelers.");
    setMeta("geo.placename", "Pembroke Pines, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "26.0078;-80.2963");
    setMeta("ICBM", "26.0078, -80.2963");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Pembroke Pines Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Pembroke Pines — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Pembroke Pines and Broward County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Pembroke Pines",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Pembroke Pines",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <PembrokePinesHeroSection />
      <PembrokePinesStickyNav />
      <PembrokePinesProcessingTimeSection />
      <PembrokePinesServicesSection />
      <PembrokePinesLocalAreaSection />
      <PembrokePinesTravelTimingSection />
      <PembrokePinesDestinationsSection />
      <PembrokePinesTestimonialsSection />
      <PembrokePinesAcceptanceFacilitiesSection />
      <PembrokePinesComparisonSection />
      <PembrokePinesMistakesSection />
      <PembrokePinesHowItWorksSection />
      <PembrokePinesFAQSection />
      <PembrokePinesHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/pembroke-pines" state="FL" />
      <PembrokePinesFinalCTA />
    </div>
  );
}